import _ from 'lodash';
import { createSelector } from 'reselect';

import { getComponentProps } from '../../form-designer/selectors/common.selectors';
import { difference } from '../../form-designer/utils/editor.utils';
import { isEmpty, isNilOrEmpty } from '../../utils/ramdaUtils';
import { REPORT_CONTROL_TYPE } from '../constants/report-designer.constants';

export const getReportModel = (state) => state.reportDesigner.reportModel;
export const getReportModelName = (state) => state.reportDesigner.name ?? '';
export const getOriginalReportModel = (state) => state.reportDesigner.originalReportModel;
export const getSelectedControlObject = (state) => state.reportDesigner.selectedControlObject;

export const getSelectedTabIndex = (state) => state.reportDesigner.selectedTabIndex;
export const getReportInfo = (state) => state.reportDesigner.reportInfo;
export const getVirtualEntityName = (state) =>
  state.reportDesigner.reportInfo?.virtualEntityName ?? '';
export const getReportModelEntityHierarchy = (state) =>
  state.reportDesigner.reportModelEntityHierarchy || [];
export const getReportModelEntityDefinition = (state) =>
  state.reportDesigner.reportModelEntityDefinition || {};
export const getEntityFieldsTranslation = (state) =>
  state.reportDesigner.entityFieldsTranslation || {};
export const getReportValidationErrors = (state) => state.reportDesigner.validationErrors || [];

export const isReportLoading = createSelector([getReportModel], (reportModel) =>
  isEmpty(reportModel)
);

export const getRootControls = createSelector([getReportModel], (reportModel) => {
  const rootContainer = Object.values(reportModel).find((control) => control.parentId === 'root');

  return rootContainer.childrenNodes.map((id) => ({
    id,
    childrenNodes: reportModel[id]?.childrenNodes || [],
  }));
});

export const getControlProps = createSelector(
  [getReportModel, getComponentProps],
  (reportModel, { id }) => reportModel[id]
);

export const getOriginalControlProps = createSelector(
  [getOriginalReportModel, getComponentProps],
  (originalReportModel, { id }) => originalReportModel[id]
);

export const getControlProperty = createSelector(
  [getReportModel, getComponentProps],
  (reportModel, { id, prop }) => _.get(reportModel, `${id}.${prop}`)
);

export const getParentChildrenControlTypes = createSelector(
  [getReportModel, getComponentProps],
  (reportModel, { parentId }) => {
    const parentChildrenNodes = reportModel[parentId].childrenNodes;

    return parentChildrenNodes.map((id) => ({
      controlType: reportModel[id].controlType,
      controlId: reportModel[id].controlId,
    }));
  }
);

export const getOriginalControlProperty = createSelector(
  [getOriginalReportModel, getComponentProps],
  (originalReportModel, { id, prop }) => _.get(originalReportModel, `${id}.${prop}`)
);

export const getSelectedControlId = createSelector(
  [getSelectedControlObject],
  (selectedControlObject) => selectedControlObject.id
);

export const getIsControlSelected = createSelector(
  [getSelectedControlId, getComponentProps],
  (selectedControlId, { id }) => selectedControlId === id
);

export const getHasControlWrapper = createSelector(
  [getSelectedControlObject, getComponentProps],
  (selectedControlObject, { id }) =>
    selectedControlObject.id === id ? selectedControlObject.hasControlWrapper : false
);

export const getIsControlPropertiesChanged = createSelector(
  [getControlProps, getOriginalControlProps],
  (props, originalProps) => {
    for (const prop in props) {
      if (isNilOrEmpty(props[prop]) && isNilOrEmpty(originalProps?.[prop])) {
        continue;
      }

      if (typeof props[prop] === 'boolean' || typeof originalProps?.[prop] === 'boolean') {
        const hasPropChanged =
          props[prop]?.toString().toLowerCase() !== originalProps?.[prop]?.toString().toLowerCase();

        if (hasPropChanged) {
          return true;
        }

        continue;
      }

      if (!_.isEqual(props[prop], originalProps?.[prop])) {
        return true;
      }
    }

    return false;
  }
);

export const getIsControlPropChanged = createSelector(
  [getControlProperty, getOriginalControlProperty],
  (prop, originalProp) => {
    if (isNilOrEmpty(prop) && isNilOrEmpty(originalProp)) {
      return false;
    }

    if (typeof prop === 'boolean' || typeof originalProp === 'boolean') {
      return prop?.toString().toLowerCase() !== originalProp?.toString().toLowerCase();
    }

    return !_.isEqual(prop, originalProp);
  }
);

export const getIsNewAddedControl = createSelector(
  [getOriginalReportModel, getComponentProps],
  (originalReportModel, { id }) => !(id in originalReportModel)
);

export const getReportConditionsTableRows = createSelector(
  [getReportModel, getSelectedControlId, getComponentProps],
  (reportModel, selectedControlId, { id }) =>
    reportModel[id].childrenNodes.map((childId) => ({
      ...reportModel[childId],
      expanded: selectedControlId === childId,
    }))
);

export const getChildrenWithControlType = createSelector(
  [getReportModel, getComponentProps],
  (reportModel, { id }) =>
    reportModel[id].childrenNodes.reduce((acc, childId) => {
      const { controlType } = reportModel[childId];

      if (Array.isArray(acc[controlType])) {
        acc[controlType].push(childId);
      } else {
        acc[controlType] = [childId];
      }

      return acc;
    }, {})
);

export const getReportModelEntitiesList = createSelector(
  [getReportModelEntityHierarchy],
  (reportModelEntityHierarchy) =>
    Object.values(reportModelEntityHierarchy).map(({ Alias, Name, TranslatedName }) => ({
      value: `${Alias}.${Name}`,
      label: TranslatedName,
    }))
);

export const getEntityFieldsBySourceFieldName = createSelector(
  [getReportModel, getReportModelEntityDefinition, getComponentProps],
  (reportModel, reportModelEntityDefinition, { id, sourceField }) => {
    const sourceFieldValue = reportModel[id][sourceField];

    const entityFields = reportModelEntityDefinition[sourceFieldValue]?.fields || [];

    return entityFields
      .map(({ fullName, translatedName }) => ({
        label: translatedName,
        value: fullName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }
);

export const getIsSaveDisabled = createSelector(
  [getReportModel, getOriginalReportModel],
  (reportModel, originalReportModel) => {
    const hasChanges = difference(reportModel, originalReportModel);

    return _.isEmpty(hasChanges);
  }
);

export const getIsPublishVirtualEntityDisabled = createSelector(
  [getReportModel],
  (reportModel = {}) => !reportModel.dataColumnContainer?.childrenNodes?.length
);

export const hasReportModelUnsavedChanges = createSelector(
  [getIsSaveDisabled],
  (isSaveDisabled) => !isSaveDisabled
);

export const getDistributionListRows = createSelector(
  [getReportModel, getReportValidationErrors],
  (reportModel, validationErrors) => {
    const distributionList = reportModel[REPORT_CONTROL_TYPE.DISTRIBUTION].distributionList;

    return distributionList.map((item) => ({
      ...item,
      errors: validationErrors.filter((error) => error.instanceId === item.id),
    }));
  }
);

export const getHasValidationErrorById = createSelector(
  [getReportValidationErrors, getComponentProps],
  (validationErrors, { id }) => !!validationErrors.find(({ instanceId }) => instanceId === id)
);

export const getHasValidationErrorByIdAndProp = createSelector(
  [getReportValidationErrors, getComponentProps],
  (validationErrors, { id, prop }) =>
    !!validationErrors.find(
      ({ instanceId, controlInfo }) =>
        //in some cases validation error may refer to more than one field
        //in this case field names are separated by "|" e.g. "dataField|imageURL" in dataColumnValidationProps
        instanceId === id && controlInfo.prop?.split('|').includes(prop)
    )
);

export const getEntityDefinitionAutocompleteFields = createSelector(
  [getReportModelEntityDefinition],
  (reportModelEntityDefinition) =>
    Object.keys(reportModelEntityDefinition).reduce((acc, entityName) => {
      reportModelEntityDefinition[entityName].fields.forEach(
        ({ translatedName = '', fullName = '' }) => {
          acc.push({
            name: `${translatedName} (${fullName})`,
            value: `{|${translatedName}|${fullName}|}`,
          });
        }
      );

      return acc;
    }, [])
);
